import {Dispatch, SetStateAction, useCallback, useMemo, useState} from 'react'
import {useAuth} from "../contexts/auth";
import {useParams} from "react-router-dom";
import Guid from "../models/Guid";

export interface IFetchDataOptions {
    body?: object,
    abortSignal?: AbortSignal,
    header?: HeadersInit,
    customPath?: string,
    returnOnly?: boolean,
    contentType?: string,
    currentValue?: unknown
    currentValueFieldName?: string
}

export interface IValidationErreur {
    type: string,
    title: string,
    status: number,
    detail: string,
    traceid: string,
    errors: object
}

export interface IApiErreur {
    AttemptedValue: string,
    propertyName: string,
    errorMessage: string,
    attemptedValue: string,
    customState: string,
    severity: number,
    errorCode: string,
    formattedMessageArguments: any[],
    formattedMessagePlaceholderValues: [{ "PropertyName": string, "PropertyValue": string }]
}

const dateFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;
const GuidFormat = /^(\{){0,1}[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}(\}){0,1}$/;

function reviver(key: any, value: string) {
    if (dateFormat.test(value)) {
        return new Date(value);
    }

    if (GuidFormat.test(value)) {
        return new Guid(value);
    }

    return value;
}

const useApi = <T, >(path?: string | undefined, defaultValue?: T) => {
    const {id} = useParams();
    const isHttps = location.protocol === "https:";
    const apiUrl = useMemo(() => process.env.NODE_ENV !== 'production' ? (isHttps ? import.meta.env["VITE_APP_API_URL_DEV_HTTPS"] : import.meta.env["VITE_APP_API_URL_DEV"]) : `${window.location.origin}/api`, []);
    const authContext = useAuth();
    const [data, setData] = useState<T | undefined>(defaultValue);
    const [erreurs, setErreurs] = useState<IApiErreur[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const fetchData = useCallback(async (method: 'post' | 'get' | 'delete' | 'put', options?: IFetchDataOptions) => {
        if (!options?.returnOnly) setIsLoading(true);
        if (!id) throw Error("id compagnie invalide!");
        try {
            // await setErreurs([]);
            var Options: RequestInit = {
                method: method,
                mode: "cors",
                headers: options?.header || {
                    "Authorization": "TOKEN " + authContext.user?.token || '',
                    "Accept-Language": 'FR',
                    "Accept": "*/*",
                    "api-key": authContext.user?.token || '',
                },
            };

            if (options?.abortSignal)
                Options = {...Options, signal: options.abortSignal}

            if (options?.body) {

                Options = {...Options, headers: {...Options.headers, "Content-Type": "application/json"}, body: JSON.stringify(options.body)}
            }
            const response = await fetch(`${apiUrl}/${id}/${options?.customPath}`, Options);
            if (!response.ok) {

                if (response.status !== 400 && response.status !== 404) {
                    const responseBody = await response.json();
                    throw Error(responseBody.detail);
                }

                if (response.status == 404)
                    return [] as IApiErreur[];

                const responseBody = await response.json();

                if (!options?.returnOnly) await setErreurs(responseBody);
                if (Array.isArray(responseBody))
                    return responseBody;
                else
                    return {errorMessage: responseBody.detail} as IApiErreur;
            }
            if (method !== 'delete') {
                let jsonData = await response.text();
                if (jsonData === "") jsonData = "{}";

                const jsonData2 = JSON.parse(jsonData, reviver)
                return jsonData2;
            }
        } catch (e: any) {
            if (e.name != "AbortError" && (options?.customPath?.toLowerCase().indexOf('getvalidation') === -1 || options?.customPath?.toLowerCase().indexOf('validatemodel') === -1)) {
                throw e;
            }
        } finally {
            if (!options?.returnOnly) setIsLoading(false)
        }
    }, [id, authContext.user?.token, apiUrl]);

    const postData = useCallback(async (body: object, customPath?: string, returnOnly?: boolean, abortSignal?: AbortSignal, header?: HeadersInit): Promise<T> => {
        const data = await fetchData("post", {body, customPath: customPath || path, abortSignal, header, returnOnly});
        if (!returnOnly)
            setData(data);
        return data as T;
    }, [fetchData, path]);

    const getData = useCallback(async <TReturn = T>(options?: IFetchDataOptions): Promise<TReturn> => {
        const currentValueFieldString = options?.currentValueFieldName ? `?${options?.currentValueFieldName}=${options?.currentValue}` : '';
        const data = await fetchData('get', {customPath: `${options?.customPath || path}${currentValueFieldString}`, abortSignal: options?.abortSignal, header: options?.header, returnOnly: options?.returnOnly});
        if (!options?.returnOnly)
            setData(data);
        return data as TReturn;
    }, [fetchData, path])

    return {
        data: data as unknown as T,
        setData: setData as Dispatch<SetStateAction<T>>,
        getData,
        postData,
        fetchData,
        erreurs,
        isLoading
    };
}

export default useApi;
